import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import { CalendarIcon, LoaderCircleIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { calculateExpectedCostForScraping } from '../../helpers/calculate-expected-cost-for-scraping';
import { useScrapingJobs } from '../../hooks/use-scraping-jobs';
import { createScrapingJob } from './createScrapingJob';

import { SOURCE_PLATFORM_TYPE } from '@/entities/public-filter/constants';
import { Button } from '@/shared/components/shadcn/button';
import { Calendar } from '@/shared/components/shadcn/calendar';
import { Checkbox } from '@/shared/components/shadcn/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/shadcn/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/components/shadcn/form';
import { Input } from '@/shared/components/shadcn/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/shadcn/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/shadcn/select';
import { Textarea } from '@/shared/components/shadcn/textarea';
import { cn } from '@/shared/utils/cn';
import { DATE_FORMATS } from '@/shared/utils/date/date-formats';
import { showErrorDialog } from '@/shared/utils/show-error-dialog';

const BaseSchema = z.object({
  platform: z.enum(SOURCE_PLATFORM_TYPE),
  keyword: z.string().min(1, 'Keyword is required.'),
  fuzzySearch: z.boolean(),
  itemsPerDay: z
    .number()
    .min(1, 'Items per day must be at least 1')
    .max(300, 'Items per day cannot exceed 300'),
  memo: z.string().optional(),
});

const OnetimeSchema = BaseSchema.extend({
  platform: z.enum(SOURCE_PLATFORM_TYPE),
  runMode: z.literal('ONETIME'),
  startDateToScrape: z.date({
    error: 'Start date is required.',
  }),
  endDateToScrape: z.date({
    error: 'End date is required.',
  }),
});

const BothSchema = BaseSchema.extend({
  platform: z.enum(SOURCE_PLATFORM_TYPE),
  runMode: z.literal('BOTH'),
  startDateToScrape: z.date({
    error: 'Start date is required.',
  }),
  endDateToScrape: z.date({
    error: 'End date is required.',
  }),
});

const PeriodicSchema = BaseSchema.extend({
  platform: z.enum(SOURCE_PLATFORM_TYPE),
  runMode: z.literal('PERIODIC'),
});

const FormSchema = z.discriminatedUnion('runMode', [OnetimeSchema, BothSchema, PeriodicSchema]);

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onExit: () => void;
  onConfirm: () => void;
}

export function GoogleSearchScrapingDialog({ isOpen, onClose, onExit, onConfirm }: Props) {
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      platform: SOURCE_PLATFORM_TYPE.TIKTOK,
      keyword: '',
      fuzzySearch: false,
      itemsPerDay: 20,
      startDateToScrape: undefined,
      endDateToScrape: new Date(),
      runMode: 'ONETIME' as const,
      memo: '',
    },
  });

  const startDateToScrape = form.watch('startDateToScrape');
  const endDateToScrape = form.watch('endDateToScrape');
  const itemsPerDay = form.watch('itemsPerDay');
  const runMode = form.watch('runMode');
  const expectedCost = useMemo(() => {
    if (!startDateToScrape || !endDateToScrape) {
      return 0;
    }

    const startDate = dayjs(startDateToScrape);
    const endDate = dayjs(endDateToScrape);

    const daysDiff = endDate.diff(startDate, 'day') + 1;
    if (daysDiff <= 0) {
      return 0;
    }

    const expectedItemCountToScrape = daysDiff * (itemsPerDay || 20);

    return calculateExpectedCostForScraping(expectedItemCountToScrape);
  }, [startDateToScrape, endDateToScrape, itemsPerDay]);

  /**
   * Keyword에 double quotes(")를 붙이지 않으면 검색 결과에 Garbage가 많이 나옵니다.
   * 그래서 default로 quotes를 붙여줍니다.
   */
  const formatKeywordWithQuotes = (keyword: string, fuzzySearch: boolean) => {
    // If fuzzy search is enabled, don't add quotes
    if (fuzzySearch) {
      return keyword;
    }

    // Remove any existing quotes and trim whitespace
    const cleanedKeyword = keyword.trim().replace(/^"+|"+$/g, '');

    // Add quotes to the keyword
    return `"${cleanedKeyword}"`;
  };

  const handleSubmit = async (data: z.infer<typeof FormSchema>) => {
    // Additional validation for date comparison
    if (data.runMode !== 'PERIODIC') {
      if (data.startDateToScrape > data.endDateToScrape) {
        form.setError('endDateToScrape', { message: 'End date must be after start date.' });
        return;
      }
    }

    setIsCreating(true);
    try {
      // Format keyword with quotes based on fuzzy search setting
      const formattedKeyword = formatKeywordWithQuotes(data.keyword, data.fuzzySearch);

      await createScrapingJob({
        platform: data.platform,
        keyword: formattedKeyword,
        itemsPerDay: data.itemsPerDay,
        startDateToScrape: data.runMode === 'PERIODIC' ? undefined : data.startDateToScrape,
        endDateToScrape: data.runMode === 'PERIODIC' ? undefined : data.endDateToScrape,
        runMode: data.runMode,
        memo: data.memo,
      });
      await useScrapingJobs.refetch('GOOGLE_SEARCH');
      onConfirm();
      onClose();
      onExit();
      form.reset();
    } catch (error) {
      await showErrorDialog({ error });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-100">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            <DialogHeader>
              <DialogTitle>Create new</DialogTitle>
              <DialogDescription>Create scraping request to attain datapoints</DialogDescription>
            </DialogHeader>

            <FormField
              control={form.control}
              name="platform"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Platform</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select platform" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={SOURCE_PLATFORM_TYPE.TIKTOK}>TikTok</SelectItem>
                      <SelectItem value={SOURCE_PLATFORM_TYPE.INSTAGRAM}>Instagram</SelectItem>
                      <SelectItem value={SOURCE_PLATFORM_TYPE.YOUTUBE}>YouTube</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex flex-col gap-4">
              <FormField
                control={form.control}
                name="keyword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Keyword</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter keyword" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="fuzzySearch"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-y-0 space-x-3">
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>⚠️ Fuzzy search</FormLabel>
                      <FormDescription className="text-xs">
                        기본적으로 Keyword에 "가 붙습니다. 이 옵션을 체크하면 안 붙입니다. 그러면
                        Garbage가 많이 나옵니다.
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="runMode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Run Mode</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select run mode" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="ONETIME">One-time only</SelectItem>
                        <SelectItem value="PERIODIC">Periodic only</SelectItem>
                        <SelectItem value="BOTH">Both one-time and periodic</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="itemsPerDay"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Items per day</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="20"
                        {...field}
                        onChange={(e) => field.onChange(parseInt(e.target.value, 10))}
                      />
                    </FormControl>
                    <p className="text-muted-foreground text-sm">Range: 1-300 items per day</p>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {runMode !== 'PERIODIC' && (
                <>
                  <FormField
                    control={form.control}
                    name="startDateToScrape"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <label className="text-sm font-medium">Start Date</label>
                        <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant={'outline'}
                                className={cn(
                                  'w-60 pl-3 text-left font-normal',
                                  !field.value && 'text-muted-foreground',
                                )}
                              >
                                {field.value ? (
                                  dayjs(field.value).format(DATE_FORMATS.base)
                                ) : (
                                  <span>Pick a start date</span>
                                )}
                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent
                            className="w-auto p-0"
                            align="start"
                            side="bottom"
                            style={{ pointerEvents: 'auto' }}
                          >
                            <Calendar
                              mode="single"
                              selected={field.value}
                              onSelect={(date) => {
                                field.onChange(date);
                                setIsDatePickerOpen(false);
                              }}
                              disabled={(date) =>
                                date > new Date() || date < new Date('1900-01-01')
                              }
                              captionLayout="dropdown"
                            />
                          </PopoverContent>
                        </Popover>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="endDateToScrape"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <label className="text-sm font-medium">End Date</label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant={'outline'}
                                className={cn(
                                  'w-60 pl-3 text-left font-normal',
                                  !field.value && 'text-muted-foreground',
                                )}
                              >
                                {field.value ? (
                                  dayjs(field.value).format(DATE_FORMATS.base)
                                ) : (
                                  <span>Pick an end date</span>
                                )}
                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent
                            className="w-auto p-0"
                            align="start"
                            side="bottom"
                            style={{ pointerEvents: 'auto' }}
                          >
                            <Calendar
                              mode="single"
                              selected={field.value}
                              onSelect={field.onChange}
                              disabled={(date) =>
                                date > new Date() || date < new Date('1900-01-01')
                              }
                              captionLayout="dropdown"
                            />
                          </PopoverContent>
                        </Popover>
                        <p className="text-muted-foreground text-sm">
                          Expected cost: ${expectedCost}
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              )}

              <FormField
                control={form.control}
                name="memo"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Memo (Optional)</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Add any notes about this scraping job..."
                        className="resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter>
              <Button variant="default" type="submit" disabled={isCreating}>
                {isCreating && <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />}
                {isCreating ? 'Creating...' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
